import "./IncomingCallNotification.css";
import React from "react";
import { Phone, PhoneOff, Video } from "lucide-react";
import AvatarImage from "../../ui/Avatar/AvatarImage";

interface IncomingCallNotificationProps {
  callerId: string;
  callerName?: string | null;
  isMeeting?: boolean;
  onAccept: () => void;
  onReject: () => void;
}

const IncomingCallNotification: React.FC<IncomingCallNotificationProps> = ({
  callerId,
  callerName,
  isMeeting = false,
  onAccept,
  onReject,
}) => {
  const displayName = callerName?.trim() || "Incoming caller";

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-5 z-[10000] flex justify-center px-4 sm:bottom-6">
      <div
        role="dialog"
        aria-live="assertive"
        aria-label={`Incoming ${isMeeting ? "meeting" : "video call"} from ${displayName}`}
        className="incoming-call-card pointer-events-auto flex w-full max-w-[440px] items-center gap-3 rounded-2xl border-[1px] border-slate-200 bg-white p-3 shadow-[0_18px_50px_rgba(15,23,42,0.18)]"
      >
        <div className="relative shrink-0">
          <AvatarImage
            size="xs"
            userId={callerId || displayName}
            displayName={displayName}
            alternative
          />
          <span className="incoming-call-badge absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border-[2px] border-white bg-[#7E57C2] text-white shadow-sm">
            <Video size={12} strokeWidth={2.4} />
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <p className="incoming-call-label m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#7E57C2]">
            {isMeeting ? "Meeting invitation" : "Incoming video call"}
          </p>
          <p className="incoming-call-name m-0 mt-0.5 truncate text-[15px] font-semibold text-slate-900">
            {displayName}
          </p>
          <p className="incoming-call-description m-0 mt-0.5 text-xs text-slate-500">
            {isMeeting ? "Ready to join the meeting?" : "Would you like to answer?"}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onReject}
            className="incoming-call-decline inline-flex h-10 w-10 items-center justify-center rounded-xl border-[1px] border-red-200 bg-red-50 text-red-600 transition hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-200"
            aria-label="Decline call"
            title="Decline"
          >
            <PhoneOff size={18} strokeWidth={2.3} />
          </button>
          <button
            type="button"
            onClick={onAccept}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-[#7E57C2] px-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#6F49B5] focus:outline-none focus:ring-2 focus:ring-[#7E57C2]/30"
            aria-label="Answer call"
          >
            <Phone size={17} strokeWidth={2.3} />
            <span className="hidden sm:inline">Answer</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncomingCallNotification;